/**
 * Comprehensive Exam Information Database
 * Contains detailed information about each exam including:
 * - About the exam
 * - Eligibility criteria
 * - Career opportunities
 * - Job prospects
 * - Salary expectations
 * - Important dates
 * - Preparation tips
 */

export const EXAM_INFO = {
  // ==================== ENGINEERING EXAMS ====================
  
  "jee_main": {
    fullName: "Joint Entrance Examination (Main)",
    conductedBy: "National Testing Agency (NTA)",
    examMode: "Computer Based Test (CBT)",
    frequency: "Twice a year (January & April)",
    applicationFee: { general: "₹1,000", female: "₹900", scst: "₹500", pwd: "₹500" },
    officialWebsite: "https://jeemain.nta.nic.in",
    totalCandidates: "13+ Lakh (Session 1, 2026)",
    totalSeats: "45,000+ across NITs, IIITs, GFTIs",
    acceptingInstitutes: 2175,
    
    about: `JEE Main is India's most competitive engineering entrance exam, conducted by NTA twice a year. It serves as the gateway to 31 NITs, 26 IIITs, and 33 GFTIs (Government Funded Technical Institutions). Over 13 lakh students appeared for the Session 1 exam in January 2026 alone. JEE Main score is also used by many state governments and private colleges for B.Tech admissions. The top 2.5 lakh rank holders qualify for JEE Advanced, the gateway to IITs.`,
    
    eligibility: {
      age: "No age limit to appear",
      qualification: "Passed 10+2 with Physics, Mathematics and Chemistry/Biology/Biotechnology/Technical Vocational subject",
      minimumMarks: "75% in Class 12 (65% for SC/ST) OR Top 20 percentile in respective board (for NIT/IIIT/GFTI admission)",
      attempts: "3 consecutive years from year of passing Class 12",
      passingYear: "Class 12 passed in 2024, 2025 or appearing in 2026",
      nationality: "Indian Citizens, NRIs, PIOs, OCIs and Foreign Nationals can apply"
    },
    
    examPattern: {
      papers: ["Paper 1 (B.E./B.Tech)", "Paper 2A (B.Arch)", "Paper 2B (B.Planning)"],
      duration: "3 hours (180 minutes)",
      totalQuestions: 75,
      attemptRequired: "75 out of 90 (each section has 20 MCQ + 10 Numerical, attempt any 5 of 10 Numerical)",
      totalMarks: 300,
      sections: ["Physics (25Q - 100 marks)", "Chemistry (25Q - 100 marks)", "Mathematics (25Q - 100 marks)"],
      marking: "+4 for correct, -1 for wrong (MCQ only), No negative for Numerical type",
      questionTypes: ["Multiple Choice Questions (MCQ)", "Numerical Value Type"]
    },
    
    importantDates: {
      session1: {
        registration: "October 28 - November 22, 2025",
        admitCard: "January 17, 2026",
        examDate: "January 21-24 & 28-29, 2026",
        answerKey: "February 4-6, 2026 (Challenge Window)",
        result: "By February 12, 2026"
      },
      session2: {
        registration: "February 1 - 25, 2026",
        admitCard: "March 2026 (Tentative)",
        examDate: "April 2-9, 2026",
        result: "April 2026 (Tentative)"
      }
    },
    
    expectedCutoff2026: {
      general: "93.5 - 96 percentile",
      obcNcl: "80 - 83 percentile",
      ews: "80 - 83 percentile",
      sc: "61 - 63.5 percentile",
      st: "48.5 - 50 percentile"
    },
    
    syllabus: {
      physics: ["Mechanics", "Thermodynamics", "Electrostatics & Current Electricity", "Optics", "Modern Physics", "Electromagnetic Induction", "Waves & Oscillations"],
      chemistry: ["Organic Chemistry (GOC, Named Reactions)", "Physical Chemistry (Thermodynamics, Equilibrium)", "Inorganic Chemistry (Coordination, p-block)", "Chemical Bonding", "Electrochemistry"],
      mathematics: ["Calculus (Integration, Differentiation)", "Coordinate Geometry", "Algebra (Complex Numbers, Matrices)", "Probability & Statistics", "Trigonometry", "3D Geometry & Vectors"]
    },
    
    careerOpportunities: [
      {
        title: "Software Engineer",
        description: "Design, develop, and maintain software applications and systems",
        avgSalary: "₹6-25 LPA",
        topRecruiters: ["Google", "Microsoft", "Amazon", "TCS", "Infosys", "Wipro"]
      },
      {
        title: "Data Scientist / AI Engineer",
        description: "Build ML models and analyze data to drive business decisions",
        avgSalary: "₹8-35 LPA",
        topRecruiters: ["Meta", "Netflix", "Flipkart", "Uber", "Swiggy"]
      },
      {
        title: "Product Manager",
        description: "Lead product development from conception to launch",
        avgSalary: "₹15-50 LPA",
        topRecruiters: ["Google", "Amazon", "Flipkart", "Paytm", "Razorpay"]
      },
      {
        title: "Mechanical / Design Engineer",
        description: "Design and optimize mechanical systems and products",
        avgSalary: "₹4-15 LPA",
        topRecruiters: ["Tata Motors", "L&T", "Mahindra", "ISRO", "BHEL"]
      },
      {
        title: "Electronics / VLSI Engineer",
        description: "Design electronic circuits, chips, and embedded systems",
        avgSalary: "₹5-20 LPA",
        topRecruiters: ["Samsung", "Intel", "Qualcomm", "Texas Instruments", "NVIDIA"]
      },
      {
        title: "Research Scientist",
        description: "Conduct cutting-edge research in science & technology",
        avgSalary: "₹8-25 LPA",
        topRecruiters: ["ISRO", "DRDO", "BARC", "IISc", "Microsoft Research"]
      }
    ],
    
    topColleges: [
      { name: "NIT Trichy", ranking: 1, cutoff: "~98.5 percentile", avgPackage: "₹12-15 LPA" },
      { name: "NIT Warangal", ranking: 2, cutoff: "~98 percentile", avgPackage: "₹12-14 LPA" },
      { name: "NIT Surathkal", ranking: 3, cutoff: "~97.5 percentile", avgPackage: "₹14-16 LPA" },
      { name: "IIIT Hyderabad", ranking: 4, cutoff: "~99 percentile", avgPackage: "₹18-22 LPA" },
      { name: "NIT Calicut", ranking: 5, cutoff: "~97 percentile", avgPackage: "₹10-12 LPA" },
      { name: "BITS Pilani (via BITSAT)", ranking: 6, cutoff: "Separate exam", avgPackage: "₹18-22 LPA" },
      { name: "VIT Vellore", ranking: 7, cutoff: "~95 percentile", avgPackage: "₹8-10 LPA" }
    ],
    
    preparationTips: [
      "Master NCERT textbooks line by line for Physics and Chemistry fundamentals",
      "Practice at least 50 questions daily across all three subjects",
      "Take full-length mock tests every week and analyze mistakes thoroughly",
      "Focus on high-weightage topics: Mechanics, Organic Chemistry, Calculus & Algebra",
      "Solve last 10 years JEE Main PYQs with timer — understand question patterns",
      "For Numerical questions, focus on calculation speed and approximation techniques",
      "Use books: HC Verma (Physics), MS Chouhan (Organic Chemistry), Cengage (Maths)",
      "Join a reputed test series (Allen, FIITJEE, Resonance) for real exam simulation"
    ],
    
    bestBooks: {
      physics: ["Concepts of Physics by HC Verma", "Fundamentals of Physics by Resnick & Halliday", "Problems in General Physics by IE Irodov"],
      chemistry: ["NCERT Chemistry (Class 11 & 12)", "Organic Chemistry by MS Chouhan", "Physical Chemistry by OP Tandon"],
      mathematics: ["Cengage Mathematics Series", "RD Sharma (Objective)", "Arihant Previous Year Papers"]
    },
    
    whyThisExam: [
      "Gateway to 31 NITs, 26 IIITs and 33 GFTIs with 45,000+ seats",
      "Accepted by 2,175+ engineering colleges across India",
      "Qualifying exam for JEE Advanced — the only path to IITs",
      "Multiple sessions (best of 2 scores considered for ranking)",
      "Many states use JEE Main for state-level engineering admissions",
      "Transparent NTA-conducted CBT with NTA percentile-based ranking"
    ],
    
    counselling: {
      process: "JoSAA (Joint Seat Allocation Authority) conducts centralized counselling for NITs, IIITs, GFTIs & IITs",
      rounds: "6-7 rounds of seat allocation",
      website: "https://josaa.nic.in",
      stateLevel: "Several states like UP, Haryana, Delhi, Rajasthan also use JEE Main for state counselling"
    }
  },

  "jee_advanced": {
    fullName: "Joint Entrance Examination (Advanced)",
    conductedBy: "One of the 23 IITs (Rotational basis)",
    examMode: "Computer Based Test (CBT)",
    frequency: "Once a year (May/June)",
    applicationFee: { general: "₹2800", female: "₹1400", scst: "₹1400" },
    officialWebsite: "https://jeeadv.ac.in",
    
    about: `JEE Advanced is the gateway to the prestigious Indian Institutes of Technology (IITs). Only the top 2,50,000 rank holders in JEE Main are eligible to appear for this exam. It is considered one of the toughest engineering entrance exams globally, testing conceptual understanding and problem-solving abilities at an advanced level.`,
    
    eligibility: {
      age: "Should be born on or after October 1, 2001",
      qualification: "Must qualify JEE Main with top 2,50,000 rank",
      attempts: "Maximum 2 consecutive years",
      additionalCriteria: "Should not have taken admission in any IIT before"
    },
    
    careerOpportunities: [
      {
        title: "Product Manager (Tech)",
        description: "Lead product development at top tech companies",
        avgSalary: "₹20-50 LPA",
        topRecruiters: ["Google", "Meta", "Microsoft", "Amazon"]
      },
      {
        title: "Quantitative Analyst",
        description: "Use mathematical models for financial analysis",
        avgSalary: "₹25-80 LPA",
        topRecruiters: ["Goldman Sachs", "Morgan Stanley", "DE Shaw", "Citadel"]
      },
      {
        title: "Management Consultant",
        description: "Advise organizations on business strategy",
        avgSalary: "₹15-40 LPA",
        topRecruiters: ["McKinsey", "BCG", "Bain", "Deloitte"]
      },
      {
        title: "AI/ML Engineer",
        description: "Develop artificial intelligence systems",
        avgSalary: "₹15-45 LPA",
        topRecruiters: ["OpenAI", "DeepMind", "NVIDIA", "Tesla"]
      },
      {
        title: "Startup Founder",
        description: "Launch innovative technology ventures",
        avgSalary: "Variable (Equity-based)",
        topRecruiters: ["Self-employed", "VC-backed"]
      }
    ],
    
    topColleges: [
      { name: "IIT Bombay", ranking: 1, cutoff: "AIR < 200 (CSE)" },
      { name: "IIT Delhi", ranking: 2, cutoff: "AIR < 300 (CSE)" },
      { name: "IIT Madras", ranking: 3, cutoff: "AIR < 400 (CSE)" },
      { name: "IIT Kanpur", ranking: 4, cutoff: "AIR < 500 (CSE)" },
      { name: "IIT Kharagpur", ranking: 5, cutoff: "AIR < 600 (CSE)" }
    ],
    
    preparationTips: [
      "Master JEE Main concepts first before attempting Advanced",
      "Focus on problem-solving skills over rote learning",
      "Practice previous 20 years' papers thoroughly",
      "Build strong conceptual foundation in all three subjects",
      "Time management is crucial - practice with strict time limits",
      "Join mock test series from reputed coaching institutes"
    ],
    
    whyThisExam: [
      "Direct admission to IITs - India's most prestigious technical institutions",
      "IIT graduates command highest salary packages",
      "Strong alumni network and placement opportunities",
      "World-class research facilities and faculty",
      "Gateway to top global universities for higher studies"
    ]
  },

  "neet_ug": {
    fullName: "National Eligibility cum Entrance Test (Undergraduate)",
    conductedBy: "National Testing Agency (NTA)",
    examMode: "Pen and Paper Based (OMR Sheet)",
    frequency: "Once a year (May)",
    applicationFee: { general: "₹1,700", obc: "₹1,600", scst: "₹1,000", pwd: "₹1,000" },
    officialWebsite: "https://neet.nta.nic.in",
    totalCandidates: "20+ Lakh annually",
    totalSeats: "1,10,000+ MBBS seats + 27,000+ BDS seats",
    acceptingInstitutes: 2319,
    
    about: `NEET UG is the single national-level entrance examination for admission to MBBS, BDS, BAMS, BHMS, BUMS, BSMS and other undergraduate medical courses across India. Conducted by NTA under the supervision of the National Medical Commission (NMC), it is one of the largest entrance exams in the world with over 20 lakh students appearing annually. NEET score is mandatory for admission to all government and private medical colleges, AIIMS, JIPMER, and AYUSH courses. The exam is conducted in 13 languages across 500+ cities in India and abroad.`,
    
    eligibility: {
      age: "Minimum 17 years as on December 31 of admission year. No upper age limit.",
      qualification: "Passed 10+2 with Physics, Chemistry, Biology/Biotechnology and English as compulsory subjects",
      minimumMarks: "50% aggregate in PCB (40% for SC/ST/OBC, 45% for PwD/General-PwD)",
      attempts: "No limit on number of attempts",
      passingYear: "Class 12 passed in 2024, 2025 or appearing in 2026",
      nationality: "Indian Nationals, NRIs, OCIs, PIOs and Foreign Nationals can apply",
      additionalNote: "Aadhaar card, UDID card and category certificates must be updated before registration"
    },
    
    examPattern: {
      mode: "Pen & Paper Based (OMR Sheet)",
      duration: "3 hours (180 minutes)",
      totalQuestions: 200,
      attemptRequired: "180 out of 200 (each section has 35 mandatory MCQs in Section A + 15 MCQs in Section B, attempt any 10 of 15)",
      totalMarks: 720,
      sections: [
        "Physics: 45 Questions (180 Marks) — Section A: 35 mandatory + Section B: attempt 10 of 15",
        "Chemistry: 45 Questions (180 Marks) — Section A: 35 mandatory + Section B: attempt 10 of 15",
        "Botany: 45 Questions (180 Marks) — Section A: 35 mandatory + Section B: attempt 10 of 15",
        "Zoology: 45 Questions (180 Marks) — Section A: 35 mandatory + Section B: attempt 10 of 15"
      ],
      marking: "+4 for correct, -1 for wrong, 0 for unattempted",
      languages: "English, Hindi, Assamese, Bengali, Gujarati, Kannada, Malayalam, Marathi, Odia, Punjabi, Tamil, Telugu, Urdu"
    },
    
    importantDates: {
      neet2026: {
        notification: "February 7, 2026 (Expected)",
        registration: "February 2026 (Expected)",
        admitCard: "April 2026 (Expected)",
        examDate: "May 3, 2026 (Expected - First Sunday of May)",
        answerKey: "May-June 2026",
        result: "June 2026 (Expected)",
        counselling: "July-August 2026 (MCC + State)"
      }
    },
    
    previousCutoff: {
      general: "720-137 marks (2025)",
      obcNcl: "136-107 marks (2025)",
      sc: "136-107 marks (2025)",
      st: "136-107 marks (2025)",
      generalPwd: "136-121 marks (2025)"
    },
    
    syllabus: {
      physics: ["Mechanics", "Thermodynamics", "Electrostatics & Current Electricity", "Optics & Modern Physics", "Electromagnetic Waves", "Semiconductor Electronics"],
      chemistry: ["Organic Chemistry (Reactions, Named Reactions, Biomolecules)", "Physical Chemistry (Solutions, Electrochemistry, Chemical Kinetics)", "Inorganic Chemistry (p-block, d-block, Coordination Compounds)"],
      biology: ["Human Physiology (Highest Weightage)", "Genetics & Evolution", "Plant Physiology", "Ecology & Environment", "Cell Biology & Biomolecules", "Animal & Plant Morphology", "Reproduction in Organisms", "Biotechnology"]
    },
    
    careerOpportunities: [
      {
        title: "Doctor (MBBS)",
        description: "Practice as general physician in hospitals and clinics",
        avgSalary: "₹8-25 LPA (₹50 LPA+ with specialization)",
        topRecruiters: ["AIIMS", "Apollo Hospitals", "Fortis", "Max Healthcare", "Medanta"]
      },
      {
        title: "Surgeon / Specialist (After MD/MS)",
        description: "Specialize in surgery, cardiology, neurology, orthopedics, etc.",
        avgSalary: "₹25 LPA - ₹1+ Cr",
        topRecruiters: ["Private Practice", "AIIMS", "Tata Memorial", "CMC Vellore"]
      },
      {
        title: "Dentist (BDS)",
        description: "Dental care, oral surgery, cosmetic dentistry",
        avgSalary: "₹4-20 LPA",
        topRecruiters: ["Dental Clinics", "Hospitals", "Clove Dental", "Private Practice"]
      },
      {
        title: "Medical Researcher",
        description: "Research diseases, develop treatments and vaccines",
        avgSalary: "₹10-30 LPA",
        topRecruiters: ["ICMR", "WHO", "Pfizer", "Biocon", "Dr. Reddy's"]
      },
      {
        title: "Government Medical Officer",
        description: "Serve in government hospitals, PHCs, and community health",
        avgSalary: "₹60,000 - ₹2,00,000/month + allowances",
        topRecruiters: ["Government Hospitals", "CGHS", "ESIC", "Railways"]
      },
      {
        title: "AYUSH Practitioner (BAMS/BHMS/BUMS)",
        description: "Practice Ayurveda, Homeopathy, or Unani medicine",
        avgSalary: "₹3-12 LPA",
        topRecruiters: ["AYUSH Ministry", "Private Clinics", "Wellness Centers"]
      }
    ],
    
    topColleges: [
      { name: "AIIMS Delhi", ranking: 1, cutoff: "AIR < 50", fees: "₹1,628/year" },
      { name: "JIPMER Puducherry", ranking: 2, cutoff: "AIR < 300", fees: "₹2,550/year" },
      { name: "CMC Vellore", ranking: 3, cutoff: "AIR < 1000", fees: "₹15,000/year" },
      { name: "AFMC Pune", ranking: 4, cutoff: "AIR < 500", fees: "Free (Service Bond)" },
      { name: "Maulana Azad Medical College, Delhi", ranking: 5, cutoff: "AIR < 100", fees: "₹5,000/year" },
      { name: "King George's Medical University, Lucknow", ranking: 6, cutoff: "AIR < 2000", fees: "₹25,000/year" },
      { name: "Grant Medical College, Mumbai", ranking: 7, cutoff: "AIR < 3000", fees: "₹30,000/year" }
    ],
    
    preparationTips: [
      "NCERT is the ultimate Bible — read Biology NCERT line by line, including diagrams and captions",
      "Focus heavily on Biology as it carries 360 marks (50% of total)",
      "Practice diagrams and labeling — they frequently appear in NEET",
      "Solve last 15 years' NEET PYQs — many questions repeat concepts",
      "For Physics, focus on Mechanics and Electrostatics (highest weightage)",
      "For Chemistry, master Organic reactions and NCERT Inorganic Chemistry",
      "Use MTG Fingertips, DC Pandey (Physics), VK Jaiswal (Inorganic) for practice",
      "Take weekly full-length mock tests with OMR sheet practice for timing"
    ],
    
    bestBooks: {
      physics: ["DC Pandey (Objective Physics)", "HC Verma (Concepts)", "NCERT Physics"],
      chemistry: ["NCERT Chemistry (Most Important)", "VK Jaiswal (Inorganic)", "MS Chouhan (Organic)", "OP Tandon (Physical)"],
      biology: ["NCERT Biology (Class 11 & 12)", "MTG Objective NCERT at Your Fingertips", "Trueman's Biology", "Pradeep's Biology"]
    },
    
    whyThisExam: [
      "The ONLY entrance exam for MBBS/BDS admission in all of India",
      "Access to 1,10,000+ MBBS seats including government colleges with ₹5K-50K/year fees",
      "Entry to prestigious AIIMS, JIPMER, AFMC institutions",
      "15% All India Quota seats + 85% State Quota seats",
      "Noble profession with highest social respect and lifetime job security",
      "Growing healthcare sector — India needs 6 lakh+ more doctors"
    ],
    
    counselling: {
      process: "MCC (Medical Counselling Committee) conducts All India Quota counselling. State counselling conducted separately by each state.",
      rounds: "4 rounds (AIQ) + Stray vacancy round",
      website: "https://mcc.nic.in",
      quota: "15% All India Quota, 85% State Quota, Deemed/Central Universities separate"
    }
  },

  "cat": {
    fullName: "Common Admission Test",
    conductedBy: "Indian Institutes of Management (IIMs)",
    examMode: "Computer Based Test (CBT)",
    frequency: "Once a year (November)",
    applicationFee: { general: "₹2400", scst: "₹1200" },
    officialWebsite: "https://iimcat.ac.in",
    
    about: `CAT is India's premier management entrance test, conducted by IIMs for admission to their flagship MBA/PGP programs. It tests verbal ability, quantitative aptitude, and data interpretation & logical reasoning. Accepted by over 1200+ B-schools across India, CAT score is valid for one year.`,
    
    eligibility: {
      age: "No age limit",
      qualification: "Bachelor's degree with 50% marks (45% for reserved categories)",
      finalYear: "Final year students can also apply",
      attempts: "No limit on attempts"
    },
    
    careerOpportunities: [
      {
        title: "Investment Banker",
        description: "Advise on mergers, acquisitions, and financial transactions",
        avgSalary: "₹25-100 LPA",
        topRecruiters: ["Goldman Sachs", "JP Morgan", "Morgan Stanley", "ICICI"]
      },
      {
        title: "Management Consultant",
        description: "Solve complex business problems for organizations",
        avgSalary: "₹20-60 LPA",
        topRecruiters: ["McKinsey", "BCG", "Bain", "Accenture"]
      },
      {
        title: "Product Manager",
        description: "Drive product strategy and development",
        avgSalary: "₹20-50 LPA",
        topRecruiters: ["Google", "Amazon", "Microsoft", "Flipkart"]
      },
      {
        title: "Marketing Manager",
        description: "Develop and execute marketing strategies",
        avgSalary: "₹15-40 LPA",
        topRecruiters: ["Unilever", "P&G", "Nestle", "Amazon"]
      },
      {
        title: "Operations Manager",
        description: "Optimize business processes and supply chain",
        avgSalary: "₹15-35 LPA",
        topRecruiters: ["Amazon", "Flipkart", "Deloitte", "Accenture"]
      }
    ],
    
    topColleges: [
      { name: "IIM Ahmedabad", ranking: 1, cutoff: "99+ percentile" },
      { name: "IIM Bangalore", ranking: 2, cutoff: "99+ percentile" },
      { name: "IIM Calcutta", ranking: 3, cutoff: "99+ percentile" },
      { name: "IIM Lucknow", ranking: 4, cutoff: "98+ percentile" },
      { name: "IIM Indore", ranking: 5, cutoff: "97+ percentile" }
    ],
    
    preparationTips: [
      "Start preparation at least 6 months before the exam",
      "Focus on reading speed for VARC section",
      "Practice mental math for Quant section",
      "Take at least 50 full-length mocks before the exam",
      "Analyze each mock test thoroughly",
      "Work on time management - 40 minutes per section"
    ],
    
    whyThisExam: [
      "Gateway to IIMs - India's best business schools",
      "MBA from IIM guarantees excellent placements",
      "Average salary at top IIMs exceeds ₹25 LPA",
      "Strong alumni network across industries",
      "Opens doors to top global companies"
    ]
  },

  "upsc_cse": {
    fullName: "Union Public Service Commission - Civil Services Examination",
    conductedBy: "Union Public Service Commission (UPSC)",
    examMode: "Written + Interview",
    frequency: "Once a year",
    applicationFee: { general: "₹100", female: "Free", scst: "Free" },
    officialWebsite: "https://upsc.gov.in",
    
    about: `UPSC CSE is India's most prestigious examination for recruitment to the All India Services and Central Services. It selects candidates for IAS, IPS, IFS, and other Group A & B services. The exam consists of three stages: Preliminary, Main, and Interview. Known for its comprehensive syllabus and low success rate (0.2-0.5%).`,
    
    eligibility: {
      age: "21-32 years (relaxation for reserved categories)",
      qualification: "Bachelor's degree from recognized university",
      attempts: "6 attempts for General (unlimited for SC/ST)",
      nationality: "Indian citizen (for IAS/IPS); others have restrictions"
    },
    
    careerOpportunities: [
      {
        title: "IAS Officer",
        description: "Administer districts, implement government policies",
        avgSalary: "₹56,100 - ₹2,50,000/month + perks",
        topRecruiters: ["Central Government", "State Governments"]
      },
      {
        title: "IPS Officer",
        description: "Lead police forces, maintain law and order",
        avgSalary: "₹56,100 - ₹2,25,000/month + perks",
        topRecruiters: ["Police Departments", "Central Armed Forces"]
      },
      {
        title: "IFS Officer",
        description: "Represent India in foreign countries",
        avgSalary: "₹56,100 - ₹2,25,000/month + perks",
        topRecruiters: ["Ministry of External Affairs"]
      },
      {
        title: "IRS Officer",
        description: "Manage taxation and revenue collection",
        avgSalary: "₹56,100 - ₹2,00,000/month + perks",
        topRecruiters: ["Income Tax Department", "Customs"]
      }
    ],
    
    topColleges: [
      { name: "LBSNAA Mussoorie", ranking: 1, cutoff: "Training Academy" },
      { name: "Sardar Patel Police Academy", ranking: 2, cutoff: "IPS Training" },
      { name: "FSI (IFS)", ranking: 3, cutoff: "IFS Training" }
    ],
    
    preparationTips: [
      "Start with NCERT books (Class 6-12) for basics",
      "Read newspapers daily (The Hindu/Indian Express)",
      "Make notes for revision - current affairs is crucial",
      "Answer writing practice for Mains is essential",
      "Join a test series for Prelims and Mains",
      "Choose optional subject wisely based on interest"
    ],
    
    whyThisExam: [
      "Most respected government job in India",
      "Power to make real impact on society",
      "Job security and excellent benefits",
      "Diverse career options within civil services",
      "High social status and recognition"
    ]
  },

  "gate": {
    fullName: "Graduate Aptitude Test in Engineering",
    conductedBy: "IITs and IISc (Rotational basis)",
    examMode: "Computer Based Test (CBT)",
    frequency: "Once a year (February)",
    applicationFee: { general: "₹1700", female: "₹850", scst: "₹850" },
    officialWebsite: "https://gate.iitb.ac.in",
    
    about: `GATE is a national-level examination that tests comprehensive understanding of undergraduate engineering subjects. It's used for M.Tech/PhD admissions to IITs, IISc, NITs and for PSU recruitment. The score is valid for 3 years.`,
    
    eligibility: {
      age: "No age limit",
      qualification: "Bachelor's degree in Engineering/Technology/Architecture or Master's in Science",
      finalYear: "Final year students can appear",
      attempts: "No limit"
    },
    
    careerOpportunities: [
      {
        title: "PSU Engineer",
        description: "Work in prestigious Public Sector Undertakings",
        avgSalary: "₹8-15 LPA (entry level)",
        topRecruiters: ["ONGC", "IOCL", "NTPC", "BHEL", "GAIL"]
      },
      {
        title: "Research Scientist",
        description: "Conduct advanced research at premier institutions",
        avgSalary: "₹10-25 LPA",
        topRecruiters: ["ISRO", "DRDO", "BARC", "IISc"]
      },
      {
        title: "M.Tech Graduate",
        description: "Specialized engineering roles after M.Tech",
        avgSalary: "₹12-30 LPA",
        topRecruiters: ["Google", "Microsoft", "Amazon", "Intel"]
      }
    ],
    
    topColleges: [
      { name: "IISc Bangalore", ranking: 1, cutoff: "850+ score" },
      { name: "IIT Bombay", ranking: 2, cutoff: "800+ score" },
      { name: "IIT Delhi", ranking: 3, cutoff: "750+ score" },
      { name: "IIT Madras", ranking: 4, cutoff: "700+ score" },
      { name: "IIT Kanpur", ranking: 5, cutoff: "650+ score" }
    ],
    
    preparationTips: [
      "Focus on your core engineering subjects",
      "Practice numerical problems extensively",
      "Revise mathematics and aptitude regularly",
      "Take topic-wise tests first, then full tests",
      "Study from standard textbooks",
      "Analyze previous 10 years' papers"
    ],
    
    whyThisExam: [
      "Gateway to M.Tech at IITs and IISc",
      "PSU recruitment with excellent packages",
      "Research opportunities at premier institutions",
      "Score valid for 3 years",
      "Multiple career options after qualifying"
    ]
  },

  "ssc_cgl": {
    fullName: "Staff Selection Commission - Combined Graduate Level",
    conductedBy: "Staff Selection Commission (SSC)",
    examMode: "Computer Based Test (CBT)",
    frequency: "Once a year",
    applicationFee: { general: "₹100", female: "Free", scst: "Free" },
    officialWebsite: "https://ssc.nic.in",
    
    about: `SSC CGL is one of the most popular government exams in India, recruiting candidates for various Group B and Group C posts in ministries, departments, and organizations under the Central Government. Posts include Inspector, Auditor, Tax Assistant, and more.`,
    
    eligibility: {
      age: "18-32 years (varies by post)",
      qualification: "Bachelor's degree from recognized university",
      attempts: "As per age limit",
      nationality: "Indian citizen"
    },
    
    careerOpportunities: [
      {
        title: "Income Tax Inspector",
        description: "Conduct tax audits and investigations",
        avgSalary: "₹44,900 - ₹1,42,400/month",
        topRecruiters: ["CBDT", "Income Tax Department"]
      },
      {
        title: "Assistant Audit Officer",
        description: "Conduct government financial audits",
        avgSalary: "₹47,600 - ₹1,51,100/month",
        topRecruiters: ["CAG Office"]
      },
      {
        title: "Preventive Officer (Customs)",
        description: "Prevent smuggling and customs evasion",
        avgSalary: "₹44,900 - ₹1,42,400/month",
        topRecruiters: ["CBIC", "Customs Department"]
      },
      {
        title: "Central Excise Inspector",
        description: "Collect and manage excise duties",
        avgSalary: "₹44,900 - ₹1,42,400/month",
        topRecruiters: ["CBIC"]
      }
    ],
    
    preparationTips: [
      "Focus on Quantitative Aptitude - high scoring",
      "Practice English comprehension daily",
      "Current affairs and GK are crucial",
      "Speed and accuracy both matter",
      "Take sectional and full mock tests",
      "Time management is key"
    ],
    
    whyThisExam: [
      "Secure government job with good salary",
      "Multiple posts to choose from",
      "Career growth opportunities",
      "Job security and benefits",
      "Work-life balance"
    ]
  },

  "clat": {
    fullName: "Common Law Admission Test",
    conductedBy: "Consortium of NLUs",
    examMode: "Computer Based Test (CBT)",
    frequency: "Once a year (December)",
    applicationFee: { general: "₹4000", scst: "₹3500" },
    officialWebsite: "https://consortiumofnlus.ac.in",
    
    about: `CLAT is the premier law entrance examination for admission to 22 National Law Universities (NLUs) in India. It tests English, Current Affairs, Legal Reasoning, Logical Reasoning, and Quantitative Techniques. NLUs are considered the best law schools in India.`,
    
    eligibility: {
      age: "No upper age limit",
      qualification: "Passed 10+2 with 45% marks (40% for reserved)",
      finalYear: "12th appearing students can apply",
      attempts: "No limit"
    },
    
    careerOpportunities: [
      {
        title: "Corporate Lawyer",
        description: "Handle corporate legal matters, M&A, contracts",
        avgSalary: "₹15-50 LPA",
        topRecruiters: ["AZB & Partners", "Cyril Amarchand", "Khaitan & Co"]
      },
      {
        title: "Litigation Lawyer",
        description: "Represent clients in court proceedings",
        avgSalary: "₹8-40 LPA",
        topRecruiters: ["Supreme Court", "High Courts", "Law firms"]
      },
      {
        title: "Legal Advisor",
        description: "Provide legal counsel to organizations",
        avgSalary: "₹12-35 LPA",
        topRecruiters: ["Reliance", "TCS", "HDFC", "Big 4 firms"]
      },
      {
        title: "Judge/Magistrate",
        description: "Preside over court proceedings",
        avgSalary: "₹80,000 - ₹2,50,000/month",
        topRecruiters: ["Judiciary via judicial services exam"]
      }
    ],
    
    topColleges: [
      { name: "NLSIU Bangalore", ranking: 1, cutoff: "AIR < 100" },
      { name: "NALSAR Hyderabad", ranking: 2, cutoff: "AIR < 200" },
      { name: "NLU Delhi", ranking: 3, cutoff: "AIR < 150" },
      { name: "NUJS Kolkata", ranking: 4, cutoff: "AIR < 300" },
      { name: "NLIU Bhopal", ranking: 5, cutoff: "AIR < 400" }
    ],
    
    preparationTips: [
      "Read newspapers daily for current affairs",
      "Practice reading comprehension extensively",
      "Develop legal reasoning through case studies",
      "Focus on logical reasoning patterns",
      "Take full-length mocks regularly",
      "Build vocabulary for English section"
    ],
    
    whyThisExam: [
      "Gateway to prestigious NLUs",
      "Law is a highly respected profession",
      "Excellent placement opportunities",
      "Diverse career paths in law",
      "Growing demand for legal professionals"
    ]
  },

  "nda": {
    fullName: "National Defence Academy Examination",
    conductedBy: "Union Public Service Commission (UPSC)",
    examMode: "Written + SSB Interview",
    frequency: "Twice a year (April & September)",
    applicationFee: { general: "₹100", scst: "Free" },
    officialWebsite: "https://upsc.gov.in",
    
    about: `NDA is the joint services academy of the Indian Armed Forces, where cadets are trained for the Army, Navy, and Air Force. The examination is conducted by UPSC and is followed by a rigorous SSB interview. It's one of the most prestigious ways to join the defense forces.`,
    
    eligibility: {
      age: "16.5 - 19.5 years",
      qualification: "10+2 for Air Force & Navy; Class 12 appearing for Army",
      physicalStandards: "Must meet physical fitness criteria",
      gender: "Male and Female candidates eligible"
    },
    
    careerOpportunities: [
      {
        title: "Army Officer",
        description: "Lead troops and manage military operations",
        avgSalary: "₹56,100 - ₹2,50,000/month + allowances",
        topRecruiters: ["Indian Army"]
      },
      {
        title: "Navy Officer",
        description: "Command naval vessels and operations",
        avgSalary: "₹56,100 - ₹2,50,000/month + allowances",
        topRecruiters: ["Indian Navy"]
      },
      {
        title: "Air Force Officer",
        description: "Pilot aircraft and manage air operations",
        avgSalary: "₹56,100 - ₹2,50,000/month + allowances",
        topRecruiters: ["Indian Air Force"]
      }
    ],
    
    preparationTips: [
      "Focus on Mathematics and General Ability Test",
      "Current affairs and general knowledge are crucial",
      "Physical fitness preparation alongside studies",
      "Practice previous years' papers",
      "Prepare for SSB interview from day one",
      "Develop leadership qualities"
    ],
    
    whyThisExam: [
      "Prestigious career in Armed Forces",
      "Adventure and challenging work",
      "Excellent salary and benefits",
      "Serve and protect the nation",
      "Retirement benefits and pensions"
    ]
  },

  "iat": {
    fullName: "IISER Aptitude Test",
    conductedBy: "Indian Institutes of Science Education and Research (IISERs)",
    examMode: "Computer Based Test (CBT)",
    frequency: "Once a year (June)",
    applicationFee: { general: "₹2000", scst: "₹1000" },
    officialWebsite: "https://www.iiseradmission.in",
    
    about: `The IISER Aptitude Test (IAT) is conducted for admission to the prestigious 5-year BS-MS dual degree program at seven IISERs across India. IISERs are premier research institutions focused on science education and research. The exam tests aptitude in Physics, Chemistry, Mathematics, and Biology.`,
    
    eligibility: {
      age: "No upper age limit",
      qualification: "10+2 with Science stream",
      minMarks: "60% aggregate in PCM/PCB",
      attempts: "Candidates must have passed 12th in current or previous year"
    },
    
    careerOpportunities: [
      {
        title: "Research Scientist",
        description: "Conduct cutting-edge scientific research",
        avgSalary: "₹10-30 LPA",
        topRecruiters: ["ISRO", "DRDO", "TIFR", "BARC", "Universities"]
      },
      {
        title: "Data Scientist",
        description: "Apply scientific methods to data analysis",
        avgSalary: "₹12-35 LPA",
        topRecruiters: ["Google", "Microsoft", "Amazon", "IBM"]
      },
      {
        title: "Professor/Academic",
        description: "Teach and conduct research at universities",
        avgSalary: "₹8-25 LPA",
        topRecruiters: ["IITs", "IISERs", "NITs", "Central Universities"]
      },
      {
        title: "Biotech Researcher",
        description: "Research in biotechnology and life sciences",
        avgSalary: "₹8-20 LPA",
        topRecruiters: ["Biocon", "Dr. Reddy's", "Pfizer", "Novartis"]
      }
    ],
    
    topColleges: [
      { name: "IISER Pune", ranking: 1, cutoff: "Top 5000 ranks" },
      { name: "IISER Kolkata", ranking: 2, cutoff: "Top 6000 ranks" },
      { name: "IISER Mohali", ranking: 3, cutoff: "Top 7000 ranks" },
      { name: "IISER Bhopal", ranking: 4, cutoff: "Top 8000 ranks" },
      { name: "IISER Thiruvananthapuram", ranking: 5, cutoff: "Top 9000 ranks" }
    ],
    
    preparationTips: [
      "Strong foundation in NCERT for all subjects",
      "Focus on conceptual understanding over memorization",
      "Practice application-based questions",
      "Balance all four subjects equally",
      "Take mock tests to gauge preparation level",
      "Read scientific magazines to develop research aptitude"
    ],
    
    whyThisExam: [
      "Gateway to India's premier research institutes",
      "Integrated 5-year BS-MS program",
      "Excellent research facilities and mentorship",
      "Opportunity for PhD and academic career",
      "Strong placement in research and industry"
    ]
  }
};

// Helper function to get exam info by ID or name
export const getExamInfo = (examIdOrName) => {
  if (!examIdOrName) return null;
  
  // First try direct key lookup
  const normalizedId = examIdOrName.toLowerCase().replace(/[\s-]/g, '_');
  if (EXAM_INFO[normalizedId]) {
    return EXAM_INFO[normalizedId];
  }
  
  // Then try matching by exam name (case insensitive)
  const searchName = examIdOrName.toLowerCase().trim();
  for (const [key, info] of Object.entries(EXAM_INFO)) {
    // Match against the key (e.g., 'jee_main' matches 'JEE Main')
    const keyNormalized = key.replace(/_/g, ' ');
    if (keyNormalized === searchName || key === searchName) {
      return info;
    }
    // Match against fullName
    if (info.fullName && info.fullName.toLowerCase().includes(searchName)) {
      return info;
    }
  }
  
  return null;
};

// Get exam info by exam name (for use with API data)
export const getExamInfoByName = (examName) => {
  if (!examName) return null;
  const searchName = examName.toLowerCase().trim();
  
  // Map common exam names to keys
  const nameToKeyMap = {
    'jee main': 'jee_main',
    'jee advanced': 'jee_advanced',
    'neet ug': 'neet_ug',
    'neet': 'neet_ug',
    'cat': 'cat',
    'upsc cse': 'upsc_cse',
    'upsc': 'upsc_cse',
    'gate': 'gate',
    'ssc cgl': 'ssc_cgl',
    'clat': 'clat',
    'nda': 'nda',
    'iat': 'iat',
    'iiser aptitude test': 'iat',
    'nest': 'nest',
    'cuet': 'cuet_ug',
    'cuet ug': 'cuet_ug',
    'ibps po': 'ibps_po',
    'sbi po': 'sbi_po',
    'rrb ntpc': 'rrb_ntpc',
  };
  
  const key = nameToKeyMap[searchName] || searchName.replace(/[\s-]/g, '_');
  return EXAM_INFO[key] || null;
};

// Get all available exam IDs
export const getAvailableExams = () => Object.keys(EXAM_INFO);
